import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Box, Button, Container, Typography } from "@mui/material";
import ExploreOutlinedIcon from "@mui/icons-material/ExploreOutlined";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import HowToRegOutlinedIcon from "@mui/icons-material/HowToRegOutlined";
import GradientIcon from "../components/GradientIcon";
import HaveQuestions from "../components/HaveQuestions";
import HandymanOutlinedIcon from "@mui/icons-material/HandymanOutlined";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EmojiObjectsOutlinedIcon from "@mui/icons-material/EmojiObjectsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import aboutBg from "../assets/about-bg.png";
import halfCircles from "../assets/half-circles.png";
import hooshyarImg from "../assets/Hooshyar.png";

const gradientHoverSx = {
  position: "relative",
  border: "2px solid transparent",
  backgroundClip: "padding-box",
  transition: "all 0.3s ease",
  "&:hover::before": {
    opacity: 1,
  },
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    borderRadius: "24px",
    padding: "2px",
    background: "linear-gradient(90deg, #FF7FAC, #5834BA)",
    WebkitMask:
      "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
    WebkitMaskComposite: "xor",
    maskComposite: "exclude",
    opacity: 0,
    transition: "opacity 0.3s ease",
  },
};

const AboutUs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Box sx={{ backgroundColor: "#F9F9F9" }}>
      <Helmet>
        <title>DAM Journey - About</title>
        <meta
          name="description"
          content="Empowering Digital Asset, Management Journeys, Helping organizations streamline, organize, and maximize the value of their digital assets through expert guidance and innovative solutions"
        />
        <meta
          property="keywords"
          content="DAM, DAM Journey, DAM Practice, DAM Consultant, DAM Consulting, DAM Practitioner, DAM Service, DAM Product, DAM Discovery, DAM Implementation, DAM Deployment, Empowering Digital Asset, Management Journeys"
        />
      </Helmet>
      <Box /*sx={{ maxWidth: "1440px" }}*/>
        {/* Hero Banner Section */}
        <Box
          sx={{
            width: "100%",
            height: "350px",
            background: "linear-gradient(0deg, #2A2177 0%, #0624BE 100%)",
            display: "flex",
            alignItems: "center",
            pl: "84px",
            pr: "219px",
            py: "74px",
            overflow: "hidden",
            position: "relative",
            "@media (max-width: 1200px)": {
              px: "24px",
              py: "78px",
            },
            "@media (max-width: 780px)": {
              flexDirection: "column",
              textAlign: "center",
              px: "16px",
              pt: "48px",
              pb: 2,
              height: "420px",
            },
          }}
        >
          {/* Left Text Content */}
          <Box
            sx={{
              flex: 2,
              zIndex: 1,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: "2.5rem",
                fontWeight: 700,
                color: "#ffffff",
                lineHeight: 1.3,
                mb: 2,
                "@media (max-width: 1200px)": {
                  fontSize: "1.75rem",
                  fontWeight: "semiBold",
                },
                "@media (max-width: 780px)": {
                  fontSize: "1.5rem",
                  mb: 1,
                },
              }}
            >
              Empowering Digital Asset
              <br />
              Management Journeys
            </Typography>
            <Typography
              sx={{
                fontSize: "20px",
                fontWeight: "regular",
                color: "#ffffff",
                lineHeight: 1.7,
                maxWidth: "624px",
                "@media (max-width: 1200px)": {
                  fontSize: "18px",
                },
                "@media (max-width: 780px)": {
                  fontSize: "16px",
                },
              }}
            >
              Helping organizations streamline, organize, and maximize the value
              of their digital assets through expert guidance and innovative
              solutions.
            </Typography>
          </Box>

          {/* Right Infographic Image */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              position: "relative",
            }}
          >
            {/* Background Half Circles */}
            <Box
              component="img"
              src={halfCircles}
              aria-hidden="true"
              sx={{
                position: "absolute",
                width: "599px",
                height: "350px",
                objectFit: "contain",
                left: -100,
                "@media (max-width: 1200px)": {
                  left: 0,
                },
                "@media (max-width: 780px)": {
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "370px",
                  height: "auto",
                },
                zIndex: 0,
              }}
            />
            {/* Foreground about-bg */}
            <Box
              component="img"
              src={aboutBg}
              sx={{
                position: "relative",
                zIndex: 1,
                width: "383px",
                height: "320px",
                "@media (max-width: 1200px)": {
                  width: "386px",
                  height: "322px",
                },
                "@media (max-width: 780px)": {
                  fontSize: "18px",
                  width: "188px",
                  height: "157px",
                },
              }}
            />
          </Box>
        </Box>

        {/* Our Story Section */}
        <Box
          sx={{
            pt: "48px",
            px: "84px",
            "@media (max-width: 1200px)": {
              px: "24px",
            },
            "@media (max-width: 780px)": {
              px: "16px",
            },
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: "3rem",
              fontWeight: "bold",
              textAlign: "center",
              color: "#2A2177",
              textAlign: "center",
              mb: 2,
              "@media (max-width: 1200px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 780px)": {
                fontSize: "2rem",
              },
            }}
          >
            Our Story
          </Typography>

          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "regular",
              textAlign: "center",
              color: "#666666",
              lineHeight: 1.7,
              mb: 5,
              "@media (max-width: 1200px)": {
                fontSize: "16px",
              },
            }}
          >
            DAM Journey was created to help businesses unlock the true potential
            of Digital Asset Management. Our mission is to simplify complex DAM
            ecosystems and guide organizations toward smarter, scalable asset
            management strategies.
          </Typography>

          {/* About the Practitioner Card */}
          <Box
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "24px",
              p: "48px",
              mb: 4,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
              "@media (max-width: 780px)": {
                flexDirection: "column",
                textAlign:"center",
                alignItems: "center",
                p: "24px",
              },
            }}
          >
            <Box
              component="img"
              src={hooshyarImg}
              sx={{
                width: "80px",
                height: "80px",
                borderRadius: "24px",
                mr: "24px",
                flexShrink: 0,
                "@media (max-width: 780px)": {
                  mr: 0,
                  mb: 2,
                },
              }}
            />
            <Box>
              <Typography
                sx={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: "#000000",
                  mb: 2,
                  "@media (max-width: 780px)": {
                    textAlign: "center",
                  },
                }}
              >
                About the Practitioner
              </Typography>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "#666666",
                  lineHeight: 1.7,
                  mb: 2,
                  "@media (max-width: 780px)": {
                    textAlign: "left",
                  },
                }}
              >
                Hooshyar Naraghi is the principal consultant at DAM Journey,
                LLC. He is a subject matter expert in digital asset management
                (DAM), content management systems (CMS), assets taxonomy and
                classification, data migrations, and training with more than two
                decades of experience across various business and engineering
                sectors. He operates a DAM practice serving clients globally.
                Some of the companies he has consulted with are Apple Computers,
                Moderna, Charter Communications, Culligan International, TD
                Ameritrade (now Charles Schwab), ServiceNow, Brocade
                Communications (now Broadcom), Altair Engineering (now Siemens),
                PLOS, AbbVie, and LINQ. He equally consulted with implementation
                vendors in the DAM/CMS space such as GSPANN, Collabera, Tech
                Mahindra, Brillio and Concentrix.
              </Typography>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "#666666",
                  lineHeight: 1.7,
                  mb: 2,
                  "@media (max-width: 780px)": {
                    textAlign: "left",
                  },
                }}
              >
                Hooshyar executes a field-tested framework in DAM product
                evaluation and selection. He also has extensive experience
                delivering Digital Asset Management (DAM) training at both
                corporate and academic levels. The DAM curriculum he developed
                and teaches is considered one of the most comprehensive programs
                in the DAM industry.
              </Typography>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "#666666",
                  lineHeight: 1.7,
                  mb: 2,
                  "@media (max-width: 780px)": {
                    textAlign: "left",
                  },
                }}
              >
                Based in Denver, Colorado, Hooshyar serves clients across the
                Americas and Europe. With a strong technical foundation and
                prior experience as a database programmer, he advises nonprofit
                institutions and educational organizations on DAM and software
                project management. Throughout his career, Hooshyar has
                developed and led product teams in building 20 CMS and CRM
                platforms. He has a U.S. patent in the handmade rug
                classification. The patent is the outcome of one of the first
                startups that developed a DAM eCommerce platform democratizing
                the market for the first time in selling handmade rugs online.
              </Typography>
              <Typography
                sx={{
                  fontSize: "18px",
                  fontWeight: 400,
                  color: "#666666",
                  lineHeight: 1.7,
                  "@media (max-width: 780px)": {
                    textAlign: "left",
                  },
                }}
              >
                Hooshyar is dedicated to enabling clients with the best-fit DAM
                solutions. Delivering exceptional consulting is central to his
                practice and a core responsibility he upholds steadfastly.
                Drawing inspiration from the professional models of law,
                medicine, and specialized craftsmanship, Hooshyar defines the
                services of DAM Journey through the discipline of a practice. In
                this spirit, he likes to say "We operate as a dedicated DAM
                practice."
              </Typography>
            </Box>
          </Box>

          {/* Mission & Vision Cards */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              "@media (max-width: 1200px)": {
                flexDirection: "column",
                px: "84px",
              },
              "@media (max-width: 780px)": {
                flexDirection: "column",
                px: "0px",
              },
              gap: 3,
              justifyContent: "center",
            }}
          >
            {/* Mission Statement Card */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                pb: "32px",
                pt: "48px",
                px: "48px",
                "@media (max-width: 780px)": {
                  px: "16px",
                  textAlign: "center",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  "@media (max-width: 780px)": {
                    flexDirection: "column",
                  },
                  gap: 3,
                  mb: 2,
                }}
              >
                <GradientIcon
                  icon={TrackChangesOutlinedIcon}
                  size={40}
                  bordered
                  boxSize={80}
                  borderRadius={24}
                  borderWidth={2}
                  angle="90"
                />
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#000000",
                      lineHeight: 2,
                    }}
                  >
                    Mission Statement
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.6,
                    }}
                  >
                    Our mission is to guide organizations through their digital
                    transformation journey by providing expert Digital Asset
                    Management solutions that centralize, organize, and optimize
                    their digital assets. We are committed to delivering
                    personalized, end-to-end support that simplifies complexity
                    and enhances productivity, ensuring our clients achieve
                    their goals with confidence and clarity.
                  </Typography>
                </Box>
              </Box>
            </Box>
            {/* Vision Statement Card */}
            <Box
              sx={{
                flex: 1,
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                pb: "32px",
                pt: "48px",
                px: "48px",
                "@media (max-width: 780px)": {
                  px: "16px",
                  textAlign: "center",
                },
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  "@media (max-width: 780px)": {
                    flexDirection: "column",
                  },
                  gap: 3,
                  mb: 2,
                }}
              >
                <GradientIcon
                  icon={VisibilityOutlinedIcon}
                  size={40}
                  bordered
                  boxSize={80}
                  borderRadius={24}
                  borderWidth={2}
                  angle="90"
                />
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography
                    sx={{
                      fontSize: "1.5rem",
                      fontWeight: 700,
                      color: "#000000",
                      lineHeight: 2,
                    }}
                  >
                    Vision Statement
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: "1rem",
                      fontWeight: 400,
                      color: "#666666",
                      lineHeight: 1.6,
                    }}
                  >
                    To be the trusted partner in Digital Asset Management,
                    recognized for our dedication to client success and our
                    ability to transform digital chaos into organized,
                    accessible, and valuable assets. We envision a world where
                    every organization can seamlessly navigate their digital
                    journey with our support, achieving digital excellence and
                    sustained growth.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* What We Do Section */}
        <Box
          sx={{
            pt: "48px",
            px: "84px",
            "@media (max-width: 1200px)": {
              px: "24px",
            },
            "@media (max-width: 780px)": {
              px: "16px",
            },
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: "3rem",
              fontWeight: "bold",
              textAlign: "center",
              color: "#2A2177",
              textAlign: "center",
              mb: 2,
              "@media (max-width: 1200px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 780px)": {
                fontSize: "2rem",
              },
            }}
          >
            What We Do
          </Typography>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "regular",
              textAlign: "center",
              color: "#666666",
              lineHeight: 1.7,
              mb: 5,
              "@media (max-width: 1200px)": {
                fontSize: "16px",
              },
            }}
          >
            We help organizations simplify and optimize their Digital Asset
            Management by designing scalable strategies, seamless integrations,
            and user-friendly experiences that drive efficiency and
            collaboration.
          </Typography>

          {/* Icons Grid */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 3,
              mb: 4,
              "@media (max-width: 1200px)": {
                flexWrap: "wrap",
              },
            }}
          >
            {/* DAM Strategy Consulting */}
            <Box
              sx={{
                textAlign: "center",
                width: "300px",
                backgroundColor: "#FFFFFF",
                borderRadius: "24px",
                py: "48px",
                px: "24px",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                "@media (max-width: 1200px)": {
                  width: "348px",
                },
                "@media (max-width: 780px)": {
                  width: "100%",
                },
              }}
            >
              <GradientIcon
                icon={ExploreOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{ margin: "0 auto 16px" }}
              />
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  mb: 1,
                  color: "#000000",
                }}
              >
                DAM Strategy Consulting
              </Typography>
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 400,
                  color: "#666666",
                  lineHeight: 1.5,
                }}
              >
                Helping businesses define the right DAM architecture and
                workflow.
              </Typography>
            </Box>

            {/* Implementation & Integration */}
            <Box
              sx={{
                textAlign: "center",
                width: "300px",
                backgroundColor: "#FFFFFF",
                borderRadius: "24px",
                py: "48px",
                px: "24px",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                "@media (max-width: 1200px)": {
                  width: "348px",
                },
                "@media (max-width: 780px)": {
                  width: "100%",
                },
              }}
            >
              <GradientIcon
                icon={ExtensionOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{ margin: "0 auto 16px" }}
              />
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  mb: 1,
                  color: "#000000",
                }}
              >
                Implementation & Integration
              </Typography>
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 400,
                  color: "#666666",
                  lineHeight: 1.5,
                }}
              >
                Seamless integration of DAM platforms with existing systems.
              </Typography>
            </Box>

            {/* User Experience Design */}
            <Box
              sx={{
                textAlign: "center",
                width: "300px",
                backgroundColor: "#FFFFFF",
                borderRadius: "24px",
                py: "48px",
                px: "24px",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                "@media (max-width: 1200px)": {
                  width: "348px",
                },
                "@media (max-width: 780px)": {
                  width: "100%",
                },
              }}
            >
              <GradientIcon
                icon={HowToRegOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{ margin: "0 auto 16px" }}
              />
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  mb: 1,
                  color: "#000000",
                }}
              >
                User Experience Design
              </Typography>
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 400,
                  color: "#666666",
                  lineHeight: 1.5,
                }}
              >
                Designing intuitive interfaces for efficient asset management.
              </Typography>
            </Box>

            {/* Optimization & Support */}
            <Box
              sx={{
                textAlign: "center",
                width: "300px",
                backgroundColor: "#FFFFFF",
                borderRadius: "24px",
                py: "48px",
                px: "24px",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                "@media (max-width: 1200px)": {
                  width: "348px",
                },
                "@media (max-width: 780px)": {
                  width: "100%",
                },
              }}
            >
              <GradientIcon
                icon={HandymanOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
                sx={{ margin: "0 auto 16px" }}
              />
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  mb: 1,
                  color: "#000000",
                }}
              >
                Optimization & Support
              </Typography>
              <Typography
                sx={{
                  fontSize: "1rem",
                  fontWeight: 400,
                  color: "#666666",
                  lineHeight: 1.5,
                }}
              >
                Continuous improvements and support to maximize DAM performance.
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Our Values Section */}
        <Box
          sx={{
            pt: "24px",
            pb: "48px",
            px: "84px",
            "@media (max-width: 1200px)": {
              px: "24px",
            },
            "@media (max-width: 780px)": {
              px: "16px",
            },
          }}
        >
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: "3rem",
              fontWeight: "bold",
              textAlign: "center",
              color: "#2A2177",
              mb: 2,
              "@media (max-width: 1200px)": {
                fontSize: "2.5rem",
              },
              "@media (max-width: 780px)": {
                fontSize: "2rem",
              },
            }}
          >
            Our Values
          </Typography>
          <Typography
            sx={{
              fontSize: "18px",
              fontWeight: "regular",
              textAlign: "center",
              color: "#666666",
              lineHeight: 1.7,
              mb: 5,
              "@media (max-width: 1200px)": {
                fontSize: "16px",
              },
            }}
          >
            Our values guide everything we do—from how we collaborate with
            clients to how we design and deliver impactful Digital Asset
            Management solutions.
          </Typography>

          {/* Cards Container */}

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: 3,
              flexWrap: "wrap",
            }}
          >
            {/* Innovation */}
            <Box
              sx={{
                width: "624px",
                height: "176px",
                backgroundColor: "#ffffff",
                borderRadius: "24px",
                display: "flex",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                alignItems: "center",
                p: "48px",
                gap: 3,
                "@media (max-width: 1200px)": {
                  width: "348px",
                  height: "288px",
                  flexDirection: "column",
                  textAlign: "center",
                },
                "@media (max-width: 780px)": {
                  width: "100%",
                },
              }}
            >
              <GradientIcon
                icon={EmojiObjectsOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#000000",
                  }}
                >
                  Innovation
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "#666666",
                    lineHeight: 1.4,
                  }}
                >
                  Constantly exploring smarter DAM solutions.
                </Typography>
              </Box>
            </Box>

            {/* Collaboration */}
            <Box
              sx={{
                width: "624px",
                height: "176px",
                backgroundColor: "#ffffff",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                borderRadius: "24px",
                display: "flex",
                alignItems: "center",
                p: "48px",
                gap: 3,
                "@media (max-width: 1200px)": {
                  width: "348px",
                  height: "288px",
                  flexDirection: "column",
                  textAlign: "center",
                },
                "@media (max-width: 780px)": {
                  width: "100%",
                },
              }}
            >
              <GradientIcon
                icon={GroupsOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#000000",
                  }}
                >
                  Collaboration
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "#666666",
                    lineHeight: 1.4,
                  }}
                >
                  Working closely with clients and teams.
                </Typography>
              </Box>
            </Box>

            {/* Transparency */}
            <Box
              sx={{
                width: "624px",
                height: "176px",
                backgroundColor: "#ffffff",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                borderRadius: "24px",
                display: "flex",
                alignItems: "center",
                p: "48px",
                gap: 3,
                "@media (max-width: 1200px)": {
                  width: "348px",
                  height: "288px",
                  flexDirection: "column",
                  textAlign: "center",
                },
                "@media (max-width: 780px)": {
                  width: "100%",
                },
              }}
            >
              <GradientIcon
                icon={TaskOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#000000",
                  }}
                >
                  Transparency
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "#666666",
                    lineHeight: 1.4,
                  }}
                >
                  Clear processes and honest communication.
                </Typography>
              </Box>
            </Box>

            {/* Excellence */}
            <Box
              sx={{
                width: "624px",
                height: "176px",
                backgroundColor: "#ffffff",
                boxShadow: "0px 2px 12px rgba(0, 0, 0, 0.08)",
                ...gradientHoverSx,
                borderRadius: "24px",
                display: "flex",
                alignItems: "center",
                p: "48px",
                gap: 3,
                "@media (max-width: 1200px)": {
                  width: "348px",
                  height: "288px",
                  flexDirection: "column",
                  textAlign: "center",
                },
                "@media (max-width: 780px)": {
                  width: "100%",
                },
              }}
            >
              <GradientIcon
                icon={WorkspacePremiumOutlinedIcon}
                size={40}
                bordered
                boxSize={80}
                borderRadius={24}
                borderWidth={2}
                angle="90"
              />
              <Box>
                <Typography
                  sx={{
                    fontSize: "1.5rem",
                    fontWeight: 700,
                    mb: 0.5,
                    color: "#000000",
                  }}
                >
                  Excellence
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    color: "#666666",
                    lineHeight: 1.4,
                  }}
                >
                  Delivering high-quality solutions and results.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Have Questions Section */}
        <HaveQuestions />
      </Box>
    </Box>
  );
};

export default AboutUs;
